import { Component, computed, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { CheckboxComponent, Option, SingleSelectDropdownComponent } from '@cumlaude/shared-components-inputs';
import { CustomExportItem, DataExport, ExportOptions, ExportType, Orientation } from '../../../services/export.service';
import { DataExportDialogComponent } from '../data-export-dialog/data-export-dialog.component';
import { intersectionBy } from 'lodash-es';
import { UserService } from '../../../services/user.service';
import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { BaseDialogComponent } from '@cumlaude/shared-components-dialogs';
import { getExportables } from '../../../services/metadata';
import { TooltipDirective } from '@cumlaude/shared-components-overlays';

@Component({
	selector: 'app-export-dialog',
	templateUrl: './export-dialog.component.html',
	styleUrls: ['./export-dialog.component.scss'],
	imports: [BaseDialogComponent, ButtonComponent, CheckboxComponent, SingleSelectDropdownComponent, TooltipDirective],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportDialogComponent implements OnInit {
	protected options = inject<ExportOptions>(DIALOG_DATA);
	private readonly dialog = inject(Dialog);
	private readonly userService = inject(UserService);
	protected readonly dialogRef = inject<DialogRef<ExportOptions>>(DialogRef);

	orientationOptions = Object.values(Orientation).map((val) => new Option<Orientation>(val));
	exportTypeOptions = this.options.exportTypes.map((val) => new Option<ExportType>(val));
	categorieen = getExportables(this.options.factTable!, (att) => !this.userService.isAttAllowed(att), this.options.attOverrides);
	selectedColumns = intersectionBy(
		this.categorieen.flatMap((categorie) => categorie.atts),
		this.options.selectedColumns,
		'att'
	);

	protected customExportItemCount = computed(() => this.options.customExportItems.filter((item) => item.condition()).length);
	protected showTitle = signal(this.options.showTitle);
	protected showFilters = signal(this.options.showFilters);
	protected showDatum = signal(this.options.showDatum);
	protected showLegenda = signal(this.options.showLegenda);
	protected type = signal(this.options.type);
	protected orientation = signal(this.options.orientation);

	aantalSelected = computed(() => {
		let aantal = 0;
		if (this.showTitle()) aantal++;
		if (this.showFilters()) aantal++;
		if (this.showDatum()) aantal++;
		if ([ExportType.PDF, ExportType.AFBEELDING].includes(this.type()) && this.showLegenda()) aantal++;
		return aantal;
	});

	changedOptions = computed(() => {
		return {
			...this.options,
			showTitle: this.showTitle(),
			showFilters: this.showFilters(),
			showDatum: this.showDatum(),
			showLegenda: this.showLegenda(),
			type: this.type(),
			orientation: this.orientation(),
		};
	});

	constructor() {
		effect(() => {
			// Bij wijziging van de export-opties, controleren of custom export items uitgeschakeld moeten worden
			const options = this.changedOptions();
			this.options.customExportItems.forEach((item) => {
				if (item.disabled?.(options)) item.condition.set(false);
			});
		});
	}

	selectedTypeOption = computed(() => {
		return this.exportTypeOptions.find(({ value }) => this.type() === value)!;
	});

	selectedOrientationOption = computed(() => {
		return this.orientationOptions.find(({ value }) => this.orientation() === value)!;
	});

	ngOnInit() {
		if (!this.options.exportTypes.includes(this.options.type)) this.type.set(this.exportTypeOptions[0].value);
	}

	openDataExportDialog() {
		this.dialog
			.open<DataExport[]>(DataExportDialogComponent, {
				data: {
					factTable: this.options.factTable,
					selection: this.selectedColumns,
					categorieen: this.categorieen,
				},
			})
			.closed.subscribe((dataExports) => {
				if (dataExports) {
					this.dialogRef.close({
						...this.changedOptions(),
						dataExports,
					});
				}
			});
	}

	getTooltip(item: CustomExportItem): string | undefined {
		if (item.disabled) {
			const reason = item.disabled(this.changedOptions());
			if (reason === false) return undefined;
			return reason;
		}
		return undefined;
	}

	protected readonly ExportType = ExportType;
}
